import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSiteContent } from "@/context/SiteContentContext";
import { deepMerge } from "@/lib/deepMerge";
import { translations, contactInfo as defaultContact } from "@/i18n/translations";
import { servicePages as defaultServicePages } from "@/i18n/services";
import { notifyContentChanged } from "@/lib/contentSync";
import { CUSTOMIZER_SECTIONS } from "@/admin/contentManifest";
import { deleteDot, formatSourceSnippet, getDot, setDot } from "@/admin/lib/payloadPaths";
import { fetchSitePayload, normalizePayload } from "@/admin/adminPayload";

function readDraftSlice(draft, section, lang, dotPath) {
  if (section.domain === "translation") return getDot(draft[lang], dotPath);
  if (section.domain === "contact") return getDot(draft.contactInfo, dotPath);
  if (section.domain === "servicePage") return getDot(draft.servicePages?.[lang]?.[section.slug], dotPath);
  return undefined;
}

function readDefault(section, lang, dotPath) {
  if (section.domain === "translation") return getDot(translations[lang], dotPath);
  if (section.domain === "contact") return getDot(defaultContact, dotPath);
  if (section.domain === "servicePage") return getDot(defaultServicePages[lang]?.[section.slug], dotPath);
  return undefined;
}

function readEffective(section, lang, draft, dotPath) {
  if (section.domain === "translation") {
    return getDot(deepMerge(translations[lang], draft[lang] || {}), dotPath);
  }
  if (section.domain === "contact") {
    return getDot(deepMerge({ ...defaultContact }, draft.contactInfo || {}), dotPath);
  }
  if (section.domain === "servicePage") {
    const base = defaultServicePages[lang]?.[section.slug] || {};
    const ov = draft.servicePages?.[lang]?.[section.slug] || {};
    return getDot(deepMerge(base, ov), dotPath);
  }
  return undefined;
}

function sourceLabel(section) {
  if (section.domain === "translation") return "translations.js";
  if (section.domain === "contact") return "translations.js (contactInfo)";
  return "services.js";
}

function applyDraftValue(draft, section, lang, dotPath, value) {
  const out = { ...draft };
  if (section.domain === "translation") {
    out[lang] = setDot(draft[lang] || {}, dotPath, value);
    return out;
  }
  if (section.domain === "contact") {
    out.contactInfo = setDot(draft.contactInfo || {}, dotPath, value);
    return out;
  }
  if (section.domain === "servicePage") {
    const sp = draft.servicePages || { ro: {}, en: {} };
    const branch = sp[lang] || {};
    const fullPath = `${section.slug}.${dotPath}`;
    out.servicePages = { ...sp, [lang]: setDot(branch, fullPath, value) };
    return out;
  }
  return out;
}

function removeDraftValue(draft, section, lang, dotPath) {
  const out = { ...draft };
  if (section.domain === "translation") {
    out[lang] = deleteDot(draft[lang] || {}, dotPath);
    return out;
  }
  if (section.domain === "contact") {
    out.contactInfo = deleteDot(draft.contactInfo || {}, dotPath);
    return out;
  }
  if (section.domain === "servicePage") {
    const sp = draft.servicePages || { ro: {}, en: {} };
    const branch = sp[lang] || {};
    const fullPath = `${section.slug}.${dotPath}`;
    out.servicePages = { ...sp, [lang]: deleteDot(branch, fullPath) };
    return out;
  }
  return out;
}

function hasDbOverride(draft, section, lang, dotPath) {
  return readDraftSlice(draft, section, lang, dotPath) !== undefined;
}

export default function AdminCustomizer() {
  const { reload } = useSiteContent();
  const [lang, setLang] = useState("ro");
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => {
    const o = {};
    CUSTOMIZER_SECTIONS.slice(0, 4).forEach((s) => {
      o[s.id] = true;
    });
    return o;
  });
  const [uploadKey, setUploadKey] = useState(null);

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const p = await fetchSitePayload();
      setDraft(p);
    } catch {
      setMsg("Nu s-a putut încărca conținutul din DB.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const onSave = async () => {
    if (!draft) return;
    setSaving(true);
    setMsg("");
    try {
      const body = normalizePayload(draft);
      const r = await fetch("/api/admin/site-save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Salvare eșuată.");
        return;
      }
      setMsg("Salvat. Site-ul public se actualizează (semnal + ~12s).");
      reload();
      notifyContentChanged();
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const onUploadForField = async (e, section, dotPath) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    const key = `${section.id}:${dotPath}`;
    setUploadKey(key);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setDraft((d) => applyDraftValue(d, section, lang, dotPath, j.url));
      setMsg(`Imagine încărcată: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploadKey(null);
    }
  };

  const sectionsForLang = useMemo(() => CUSTOMIZER_SECTIONS, []);

  if (loading || !draft) {
    return (
      <div className="mx-auto max-w-6xl py-16 text-center text-sm text-white/50">
        {loading ? "Se încarcă datele din baza de date…" : "Nu există date de afișat."}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">Personalizare conținut</h1>
        <p className="max-w-3xl text-sm text-white/50">
          Vezi pentru fiecare câmp sursa din cod (implicit) și valoarea afișată acum pe site (după merge cu
          override-urile din DB). Modificările se salvează în aceeași structură ca la JSON expert — poți combina ambele
          moduri.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-white/40">Limbă editată</span>
        <div className="flex rounded-full border border-white/15 p-0.5">
          {["ro", "en"].map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                lang === l ? "bg-[#fbd9e5] text-[#3e2723]" : "text-white/60 hover:text-white"
              }`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={loadDraft}
          className="ml-auto rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:bg-white/5"
        >
          Reîncarcă din DB
        </button>
      </div>

      <div className="space-y-3">
        {sectionsForLang.map((section) => {
          const isOpen = open[section.id];
          return (
            <section
              key={section.id}
              className="overflow-hidden rounded-2xl border border-white/10 bg-black/25 shadow-sm"
            >
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [section.id]: !o[section.id] }))}
                className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left hover:bg-white/5"
              >
                <div>
                  <h2 className="text-base font-semibold text-[#f5f0ee]">{section.title}</h2>
                  {section.subtitle ? (
                    <p className="mt-1 text-xs text-white/45">{section.subtitle}</p>
                  ) : null}
                  <p className="mt-2 text-[11px] text-white/35">
                    Sursă implicită: <span className="font-mono text-white/50">{sourceLabel(section)}</span>
                    {section.domain === "servicePage" ? (
                      <>
                        {" "}
                        · slug <span className="font-mono text-white/50">{section.slug}</span>
                      </>
                    ) : null}
                  </p>
                </div>
                <span className="shrink-0 text-white/40">{isOpen ? "▼" : "▶"}</span>
              </button>
              {isOpen ? (
                <div className="space-y-4 border-t border-white/10 px-4 py-4">
                  {section.fields.map((field) => {
                    const def = readDefault(section, lang, field.path);
                    const eff = readEffective(section, lang, draft, field.path);
                    const custom = hasDbOverride(draft, section, lang, field.path);
                    const strVal = eff === undefined || eff === null ? "" : String(eff);
                    const ft = field.type || "text";
                    const uploadBusy = uploadKey === `${section.id}:${field.path}`;

                    return (
                      <div
                        key={field.path}
                        className="rounded-xl border border-white/10 bg-[#1a1514]/80 p-4 space-y-2"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-white/90">{field.label}</span>
                          {custom ? (
                            <span className="rounded-full bg-[#fbd9e5]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#fbd9e5]">
                              Suprascris în payload
                            </span>
                          ) : (
                            <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/40">
                              Doar din cod
                            </span>
                          )}
                        </div>
                        <div className="grid gap-1 text-[11px] text-white/45 sm:grid-cols-2">
                          <div>
                            <span className="text-white/35">Implicit (cod): </span>
                            <span className="font-mono text-white/55" title={String(def)}>
                              {formatSourceSnippet(def)}
                            </span>
                          </div>
                          <div>
                            <span className="text-white/35">Actual pe site: </span>
                            <span className="font-mono text-[#fbd9e5]/80" title={strVal}>
                              {formatSourceSnippet(eff)}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                          {ft === "textarea" ? (
                            <textarea
                              value={strVal}
                              onChange={(e) => setDraft((d) => applyDraftValue(d, section, lang, field.path, e.target.value))}
                              rows={3}
                              className="min-h-[72px] w-full flex-1 rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-[#e8e0dc] outline-none focus:ring-2 focus:ring-[#fbd9e5]/25"
                            />
                          ) : (
                            <input
                              type={ft === "image" ? "url" : "text"}
                              value={strVal}
                              onChange={(e) =>
                                setDraft((d) => applyDraftValue(d, section, lang, field.path, e.target.value))
                              }
                              placeholder={ft === "image" ? "https://… sau /api/uploads/…" : ""}
                              className="w-full flex-1 rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-[#e8e0dc] outline-none focus:ring-2 focus:ring-[#fbd9e5]/25"
                            />
                          )}
                          <div className="flex shrink-0 flex-wrap gap-2">
                            {ft === "image" ? (
                              <label className="cursor-pointer rounded-full border border-white/20 px-3 py-2 text-xs text-white/80 hover:bg-white/5">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/gif"
                                  className="sr-only"
                                  disabled={uploadBusy || saving}
                                  onChange={(e) => onUploadForField(e, section, field.path)}
                                />
                                {uploadBusy ? "…" : "Upload"}
                              </label>
                            ) : null}
                            <button
                              type="button"
                              disabled={!custom}
                              onClick={() => setDraft((d) => removeDraftValue(d, section, lang, field.path))}
                              className="rounded-full border border-white/15 px-3 py-2 text-xs text-white/60 hover:bg-white/5 disabled:opacity-30"
                            >
                              Resetează
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      {msg ? <p className="text-sm text-[#fbd9e5]/90">{msg}</p> : null}

      <div className="flex flex-wrap gap-3 pb-8">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-full bg-[#fbd9e5] px-8 py-3 text-sm font-semibold text-[#3e2723] disabled:opacity-50"
        >
          {saving ? "Se salvează…" : "Salvează tot în baza de date"}
        </button>
        <p className="max-w-xl text-xs text-white/40">
          Câmpurile neincluse aici rămân editabile din JSON expert. Nu șterge accidental chei pe care nu le vezi în
          personalizare — folosește Reîncarcă din DB după editări în JSON.
        </p>
      </div>
    </div>
  );
}
